import { useState } from 'react';
import { Loader2, ThumbsUp, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { formatDate } from '@/lib/utils';
import { formatArr } from '../format';
import { CustomerName } from './CustomerName';
import { CATEGORIES, CATEGORY_LABEL, STATUSES } from '../constants';
import {
  useAddAsk, useDeleteCustomerRequest, useRemoveAsk, useSetCustomerRequestStatus, useUpdateCustomerRequest,
} from '../hooks/useCustomerRequestMutations';
import { CustomerPicker } from './CustomerPicker';
import { CustomerRequestStatusBadge } from './CustomerRequestStatusBadge';
import type { AskDraft, CustomerRequestCategory, CustomerRequestDetail, CustomerRequestStatus } from '../types';

const NO_CATEGORY = '__none__';

interface Props {
  request: CustomerRequestDetail;
  /** PG id of the session user. */
  meId: string | undefined;
  canChangeStatus: boolean;
  /** Customer and note carried over from "Upvote this" in the new-request form. */
  initialAsk?: AskDraft;
  onDeleted: () => void;
}

/** Mounted once per request (keyed by id), so local drafts never leak between requests. */
export function RequestDetailBody({ request, meId, canChangeStatus, initialAsk, onDeleted }: Props) {
  const canEdit = canChangeStatus || request.createdBy.id === meId;
  const update = useUpdateCustomerRequest();
  const save = (data: Parameters<typeof update.mutate>[0]['data']) => update.mutate({ id: request.id, data });

  // Local copies for autosave-on-blur.
  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(request.description ?? '');

  return (
    <div className="p-4 space-y-5 text-sm">
      <div className="flex items-baseline gap-3">
        <span data-testid="request-votes" className="text-2xl font-bold text-primary tabular-nums">
          {request.votes} {request.votes === 1 ? 'vote' : 'votes'}
        </span>
        <span className="text-muted-foreground">
          {request.customerCount} {request.customerCount === 1 ? 'customer' : 'customers'}
          {' · '}{request.revenue ? `ARR ${formatArr(request.revenue)}` : 'no revenue yet'}
          {' · '}{request.activeSensors ? `${request.activeSensors} active sensors` : 'no active sensors'}
        </span>
      </div>

      <StatusSection request={request} canChangeStatus={canChangeStatus} />

      {canEdit ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cr-edit-title">Title</Label>
            <Input
              id="cr-edit-title"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => { if (title.trim() && title.trim() !== request.title) save({ title: title.trim() }); }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cr-edit-description">Description</Label>
            <Textarea
              id="cr-edit-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => { if (description.trim() !== (request.description ?? '')) save({ description: description.trim() }); }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select
              value={request.category ?? NO_CATEGORY}
              onValueChange={(v) => save({ category: v === NO_CATEGORY ? null : v as CustomerRequestCategory })}
            >
              <SelectTrigger aria-label="Category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          {request.category && <p className="text-xs text-muted-foreground">{CATEGORY_LABEL[request.category]}</p>}
          {request.description && <p className="whitespace-pre-wrap">{request.description}</p>}
        </div>
      )}

      <AddAskBox request={request} initial={initialAsk} />

      <WhoAsked request={request} meId={meId} canChangeStatus={canChangeStatus} />

      <p className="text-xs text-muted-foreground">
        Created by {request.createdBy.name || '—'} on {formatDate(request.createdAt)}
      </p>

      {canChangeStatus && <DeleteRequest id={request.id} onDeleted={onDeleted} />}
    </div>
  );
}

function StatusSection({ request, canChangeStatus }: { request: CustomerRequestDetail; canChangeStatus: boolean }) {
  const setStatus = useSetCustomerRequestStatus();
  const [pending, setPending] = useState<CustomerRequestStatus | null>(null);
  const [note, setNote] = useState('');

  return (
    <div className="space-y-2">
      <Label>Status</Label>
      {canChangeStatus ? (
        <Select value={pending ?? request.status} onValueChange={(v) => setPending(v as CustomerRequestStatus)}>
          <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div><CustomerRequestStatusBadge status={request.status} testId="request-status-badge" /></div>
      )}

      {pending && pending !== request.status && (
        <div className="space-y-2 rounded-md border p-3">
          <Label htmlFor="cr-status-note">Note to the Account Managers</Label>
          <Textarea
            id="cr-status-note"
            rows={2}
            value={note}
            placeholder="Optional: why, or which release"
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Everyone who logged a vote and each customer's AM gets an email.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setPending(null)}>Cancel</Button>
            <Button
              type="button"
              size="sm"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate(
                { id: request.id, status: pending, note: note.trim() || undefined },
                { onSuccess: () => { setPending(null); setNote(''); } },
              )}
            >
              {setStatus.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save status
            </Button>
          </div>
        </div>
      )}

      {request.statusNote && (
        <p className="text-muted-foreground whitespace-pre-wrap">
          {request.statusNote}
          {request.statusChangedBy && ` — ${request.statusChangedBy.name}, ${formatDate(request.statusChangedAt)}`}
        </p>
      )}
    </div>
  );
}

function AddAskBox({ request, initial }: { request: CustomerRequestDetail; initial?: AskDraft }) {
  const add = useAddAsk();
  const [accountId, setAccountId] = useState<string | null>(initial?.accountId ?? null);
  const [note, setNote] = useState(initial?.note ?? '');
  const alreadyVoted = !!accountId && request.asks.some((a) => a.accountId === accountId);

  return (
    <div className="rounded-md border border-dashed border-primary/60 p-3 space-y-2">
      <p className="font-medium">+1 for a customer</p>
      <CustomerPicker value={accountId} onChange={setAccountId} disabled={add.isPending} />
      <Input aria-label="What they said" value={note} placeholder="What did they say? (optional)" onChange={(e) => setNote(e.target.value)} />
      {alreadyVoted && <p className="text-xs text-muted-foreground">This customer already voted: this adds to the history, not to the count.</p>}
      <Button
        type="button"
        className="w-full"
        disabled={add.isPending}
        onClick={() => add.mutate(
          { id: request.id, data: { accountId, note: note.trim() || undefined } },
          { onSuccess: () => { setAccountId(null); setNote(''); } },
        )}
      >
        <ThumbsUp className="h-4 w-4 mr-2" />
        {alreadyVoted ? 'Add to history' : 'Upvote'}
      </Button>
    </div>
  );
}

function WhoAsked({ request, meId, canChangeStatus }: { request: CustomerRequestDetail; meId: string | undefined; canChangeStatus: boolean }) {
  const remove = useRemoveAsk();
  return (
    <div className="space-y-1">
      <p className="font-medium">Who asked</p>
      <ul aria-label="Who asked" className="divide-y">
        {request.asks.map((a) => (
          <li key={a.id} className="py-2 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p>
                <span className="font-medium">
                  {a.accountName ? <CustomerName name={a.accountName} status={a.accountStatus} /> : <i className="text-muted-foreground">No customer named</i>}
                </span>
                {a.accountTier && <span className="text-xs text-muted-foreground"> · {a.accountTier}</span>}
                {!!a.accountRevenue && <span className="text-xs text-muted-foreground"> · ARR {formatArr(a.accountRevenue)}</span>}
                {!a.countsAsVote && <span className="text-xs text-muted-foreground"> · asked again</span>}
              </p>
              <p className="text-xs text-muted-foreground">{a.recordedBy.name || '—'} · {formatDate(a.createdAt)}</p>
              {a.note && <p className="text-xs mt-0.5 whitespace-pre-wrap">"{a.note}"</p>}
            </div>
            {(canChangeStatus || a.recordedBy.id === meId) && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label="Remove this vote"
                disabled={remove.isPending}
                onClick={() => remove.mutate({ id: request.id, askId: a.id })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeleteRequest({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const del = useDeleteCustomerRequest();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="text-destructive">
          <Trash2 className="h-4 w-4 mr-2" />
          Delete request
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this request?</AlertDialogTitle>
          <AlertDialogDescription>It and every vote on it are removed for everyone. This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => del.mutate(id, { onSuccess: onDeleted })}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
